'use client';

import React, { useState, useEffect } from 'react';
import { motion, LazyMotion, domAnimation } from 'framer-motion';
import Link from 'next/link';
import Head from 'next/head';
import { 
  Card,
  CardContent,
  Button,
  SkeletonLoader,
  Text,
  Heading,
} from '../ui/design-system';
import { LegalHero } from './LegalHero';
import { LegalContactInfo } from './LegalContactInfo';

import { cn } from '@/lib/utils';
import '../../styles/legal-content.css';

interface LegalPageProps {
  title: string;
  description?: string;
  heroBannerSrc?: string;
  heroBannerAlt?: string;
  lastUpdated?: string;
  children: React.ReactNode;
  seoTitle?: string;
  seoDescription?: string;
  className?: string;
  hideTitleInContent?: boolean;
  showContactInfo?: boolean;
}

/**
 * LegalPage Component
 * 
 * A premium layout component for all legal pages with elegant styling,
 * subtle animations, and semantic markup.
 */
export function LegalPage({ 
  title, 
  description,
  heroBannerSrc, 
  heroBannerAlt,
  lastUpdated, 
  children,
  seoTitle,
  seoDescription,
  className,
  hideTitleInContent,
  showContactInfo,
}: LegalPageProps) {
  const [isLoading, setIsLoading] = useState(true);

  // Reduced loading time to improve navigation experience
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300); // Reduced from 800ms to 300ms
    
    return () => clearTimeout(timer);
  }, []);
  
  // Content animation variants with reduced animation durations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.05, // Reduced from 0.1 to 0.05
        duration: 0.2 // Reduced from 0.3 to 0.2
      } 
    }
  };
  
  // Item animation variants
  const itemVariants = {
    hidden: { y: 10, opacity: 0 }, // Reduced y from 20 to 10
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        duration: 0.3, // Reduced from 0.5 to 0.3
        ease: [0.22, 1, 0.36, 1]
      } 
    }
  };

  // Preload legal page CSS for fast transitions
  useEffect(() => {
    // Preload the other legal pages
    const pages = ['/privacy', '/terms', '/cookies', '/shipping', '/refund'];
    pages.forEach(page => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = page;
      document.head.appendChild(link);
    });
  }, []);

  return (
    <>
      <Head>
        {/* Add preconnect for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        
        {/* SEO metadata */}
        <title>{seoTitle || `${title} | PAVISHA`}</title>
        <meta name="description" content={seoDescription || description || `${title} for PAVISHA products and services`} />
        
        {/* Open Graph metadata */}
        <meta property="og:title" content={seoTitle || `${title} | PAVISHA`} />
        <meta property="og:description" content={seoDescription || description || `${title} for PAVISHA products and services`} />
        {heroBannerSrc && <meta property="og:image" content={heroBannerSrc} />}
        <meta property="og:type" content="website" />
        
        {/* Twitter metadata */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle || `${title} | PAVISHA`} />
        <meta name="twitter:description" content={seoDescription || description || `${title} for PAVISHA products and services`} />
        {heroBannerSrc && <meta name="twitter:image" content={heroBannerSrc} />}
      </Head>

      <main itemScope itemType="https://schema.org/WebPage" className="w-full">
        {/* Hero Section using LegalHero component */}
        {!isLoading && (
          <LegalHero
            title={title}
            description={description}
            imageSrc={heroBannerSrc}
            imageAlt={heroBannerAlt || `${title} Banner`}
            lastUpdated={lastUpdated}
          />
        )}
        
        <div className="flex justify-center w-full bg-white dark:bg-gray-900 pt-4 pb-12">
          <div id="legal-content-root" className="w-full max-w-5xl px-4 sm:px-6 lg:px-8">
            {/* Use LazyMotion to reduce initial bundle size */}
            {/* Animated version for screen */}
            <div className="animated-content-wrapper">
              <LazyMotion features={domAnimation}>
                {isLoading ? (
                  <div className="space-y-8 w-full">
                    {/* Skeleton loader for the hero section */}
                    <div className="text-center space-y-6">
                      <SkeletonLoader className="h-12 w-2/3 mx-auto" variant="shimmer" />
                      <SkeletonLoader className="h-4 w-1/3 mx-auto" variant="shimmer" />
                    </div>
                    
                    {/* Skeleton loader for the content card */}
                    <Card className="shadow-lg w-full premium-radius-xl">
                      <CardContent className="p-6 space-y-6">
                        <div className="space-y-4">
                          <SkeletonLoader className="h-6 w-1/3" variant="shimmer" />
                          <div className="space-y-2">
                            <SkeletonLoader className="h-4 w-full" variant="shimmer" />
                            <SkeletonLoader className="h-4 w-full" variant="shimmer" />
                            <SkeletonLoader className="h-4 w-5/6" variant="shimmer" />
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <SkeletonLoader className="h-6 w-1/4" variant="shimmer" />
                          <div className="space-y-2">
                            <SkeletonLoader className="h-4 w-full" variant="shimmer" />
                            <SkeletonLoader className="h-4 w-full" variant="shimmer" />
                            <SkeletonLoader className="h-4 w-4/5" variant="shimmer" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={containerVariants}
                  className="space-y-8 w-full"
                >                
                  {/* Legal page content card */}
                  <motion.div variants={itemVariants} className="w-full">
                    <Card 
                      className="w-full relative overflow-visible premium-radius-2xl border-0 shadow-2xl bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl"
                    >
                      {/* Animated border/glow */}
                      <div className="absolute -inset-2 z-0 rounded-[2rem] pointer-events-none bg-gradient-to-tr from-primary-400 via-pink-300 to-primary-200 dark:from-primary-700 dark:via-fuchsia-700 dark:to-primary-400 opacity-60 blur-xl animate-pulse-slow" />
                      <CardContent className="p-6">
                        <div id="legal-content-root" className="mt-8">
                          {children}
                          
                          {showContactInfo && 
                            <LegalContactInfo 
                              title="Contact Information"
                              introText="For any questions or concerns, please contact us at:"
                              companyName="PAVISHA"
                              addressLines={[
                                "Pavisha Pet Industries",
                                "Mahadeo Asthan, March Marchi Rd",
                                "near Bypass Thana, Paijawa",
                                "Patna, Bihar 800007",
                                "India"
                              ]}
                              phone="+91 79799 12640"
                              phoneHref="tel:+917979912640"
                              email="info@pavisha.com"
                              emailHref="mailto:info@pavisha.com"
                              className="mb-6 mt-6 pt-6 md:pt-12 print:hidden"
                            />
                          }
                          
                          {lastUpdated && (
                            <div className="mt-8 text-sm text-gray-500">
                              Last updated: {lastUpdated}
                            </div>
                          )}
                          
                          {/* Hidden contact info for print */}
                          <div className="hidden print:block mt-8 pt-6 border-t border-gray-200">
                            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                            <p className="mb-2">
                              <strong>Address:</strong> Plot No. 35-50, GIDC, Ankleshwar, Gujarat - 393002, India
                            </p>
                            <p className="mb-2">
                              <strong>Phone:</strong> <a href="tel:+917979912640" className="text-primary">+91 79799 12640</a>
                            </p>
                            <p>
                              <strong>Email:</strong> <a href="mailto:sales@pavisha.com" className="text-primary">sales@pavisha.com</a>
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                  </motion.div>
                )}
              </LazyMotion>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}